import { Card } from "@/components/layout";
import {
  MetricKpiTile,
  PanelError,
  PanelLoading,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { HorizontalBarList, MultiLineChart, type LineSeries } from "@/components/charts";
import { useGa4ByDayOfWeek, useGa4ByHour, useGa4Trend, type Ga4Device } from "@/hooks/useGa4";
import type { TimeRange } from "@/lib/time-range";

interface Props {
  range: TimeRange;
  device?: Ga4Device | null;
}

/**
 * GA4 traffic patterns (real production data): the daily demand trend plus the
 * busiest hours and days — i.e. "Peak traffic by hour/day" (UC5 §7 KPI).
 * Honours the device segment filter (FR-09).
 */
export function Ga4TrafficPatternsSection({ range, device = null }: Props) {
  const trend = useGa4Trend(range, { device });
  const byHour = useGa4ByHour(range, { device });
  const byDow = useGa4ByDayOfWeek(range, { device });

  const series: LineSeries[] = [
    { name: "Active users", tone: "sky", points: (trend.data?.points ?? []).map((p) => ({ bucket: p.date, value: p.activeUsers })) },
    { name: "New users", tone: "emerald", points: (trend.data?.points ?? []).map((p) => ({ bucket: p.date, value: p.newUsers })) },
    { name: "Sessions", tone: "amber", points: (trend.data?.points ?? []).map((p) => ({ bucket: p.date, value: p.sessions })) },
  ];

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="GA4 Traffic Patterns"
        source="ga4"
        description="When demand actually happens. The daily trend shows how traffic moves over the selected range; the busiest-hours and busiest-days breakdowns reveal peak load windows for capacity planning."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MetricKpiTile
          label="Busiest hour"
          value={byHour.data?.peak ? byHour.data.peak.label : "—"}
          delta={byHour.data?.peak ? `${byHour.data.peak.users.toLocaleString("en-US")} active users` : undefined}
          tone="sky"
          hint="Hour of day (site timezone) with the most active users across the range — the daily peak-load window."
          isLoading={byHour.isLoading}
          errorTitle={byHour.error ? panelErrorTitle(byHour.error) : undefined}
        />
        <MetricKpiTile
          label="Busiest day"
          value={byDow.data?.peak ? byDow.data.peak.label : "—"}
          delta={byDow.data?.peak ? `${byDow.data.peak.users.toLocaleString("en-US")} active users` : undefined}
          tone="emerald"
          hint="Day of week with the most active users across the range."
          isLoading={byDow.isLoading}
          errorTitle={byDow.error ? panelErrorTitle(byDow.error) : undefined}
        />
        <MetricKpiTile
          label="Peak day in range"
          value={trend.data?.peak ? new Date(trend.data.peak.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
          delta={trend.data?.peak ? `${trend.data.peak.activeUsers.toLocaleString("en-US")} active users` : undefined}
          tone="neutral"
          hint="The single calendar day with the highest active users in the selected range."
          isLoading={trend.isLoading}
          errorTitle={trend.error ? panelErrorTitle(trend.error) : undefined}
        />
      </div>

      <Card title="Daily traffic trend">
        {trend.isLoading ? <PanelLoading />
          : trend.error ? <PanelError error={trend.error} onRetry={() => trend.refetch()} />
          : <MultiLineChart series={series} />}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Active users by hour of day">
          {byHour.isLoading ? <PanelLoading />
            : byHour.error ? <PanelError error={byHour.error} onRetry={() => byHour.refetch()} />
            : <HorizontalBarList tone="sky" limit={24}
                entries={(byHour.data?.entries ?? []).map((e) => ({ label: e.label, value: e.users }))} />}
        </Card>
        <Card title="Active users by day of week">
          {byDow.isLoading ? <PanelLoading />
            : byDow.error ? <PanelError error={byDow.error} onRetry={() => byDow.refetch()} />
            : <HorizontalBarList tone="emerald" limit={7}
                entries={(byDow.data?.entries ?? []).map((e) => ({ label: e.label, value: e.users }))} />}
        </Card>
      </div>
    </section>
  );
}
